/**
 * ScenarioMetricsDisplay Component - Shows scenario impact metrics
 * Requirements: 3.2, 3.3, 3.4, 3.5, 6.4
 */

import { ScenarioMetrics } from '../services/api';

interface ScenarioMetricsDisplayProps {
  metrics: ScenarioMetrics | null;
  baselineCostPerCase: number;
  baselineTotalCases: number;
  isModified: boolean;
}

function ScenarioMetricsDisplay({
  metrics,
  baselineCostPerCase,
  baselineTotalCases,
  isModified,
}: ScenarioMetricsDisplayProps) {
  const formatCurrency = (amount: number): string => {
    return `$${amount.toFixed(2)}`;
  };

  const formatLargeCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}MM`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatPercent = (num: number): string => {
    return `${(num * 100).toFixed(1)}%`;
  };

  if (!isModified) {
    return (
      <div style={styles.placeholder}>
        <p style={styles.placeholderText}>
          Adjust the sliders above to model different scenarios
        </p>
        <div style={styles.baselineInfo}>
          <div style={styles.baselineItem}>
            <span style={styles.baselineLabel}>Current Cost/Case</span>
            <span style={styles.baselineValue}>{formatCurrency(baselineCostPerCase)}</span>
          </div>
          <div style={styles.baselineItem}>
            <span style={styles.baselineLabel}>Total Cases</span>
            <span style={styles.baselineValue}>{formatNumber(baselineTotalCases)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return <div style={styles.placeholder}>Loading metrics...</div>;
  }

  const savingsPositive = metrics.savingsPerCase > 0;
  const extendedValuePositive = metrics.totalExtendedValue > 0;

  return (
    <div style={styles.container}>
      {/* Cost Per Case Comparison */}
      <div style={styles.comparisonSection}>
        <div style={styles.comparisonRow}>
          <div style={styles.metricBox}>
            <span style={styles.metricLabel}>Current Cost/Case</span>
            <span style={styles.metricValue}>{formatCurrency(metrics.currentCostPerCase)}</span>
          </div>
          <div style={styles.arrow}>→</div>
          <div style={styles.metricBox}>
            <span style={styles.metricLabel}>Scenario Cost/Case</span>
            <span style={styles.metricValue}>{formatCurrency(metrics.scenarioCostPerCase)}</span>
          </div>
        </div>
      </div>

      {/* Savings Highlight */}
      <div style={{
        ...styles.savingsBox,
        backgroundColor: savingsPositive ? '#d4edda' : '#f8d7da',
        borderColor: savingsPositive ? '#28a745' : '#dc3545',
      }}>
        <span style={styles.savingsLabel}>
          {savingsPositive ? 'Savings Per Case' : 'Additional Cost Per Case'}
        </span>
        <span style={{
          ...styles.savingsValue,
          color: savingsPositive ? '#28a745' : '#dc3545',
        }}>
          {savingsPositive ? '-' : '+'}{formatCurrency(Math.abs(metrics.savingsPerCase))}
        </span>
      </div>

      {/* Extended Value */}
      <div style={{
        ...styles.extendedValueBox,
        backgroundColor: extendedValuePositive ? '#cce5ff' : '#f8d7da',
      }}>
        <span style={styles.extendedValueLabel}>Total Extended Value</span>
        <span style={{
          ...styles.extendedValueAmount,
          color: extendedValuePositive ? '#004085' : '#721c24',
        }}>
          {extendedValuePositive ? '+' : ''}{formatLargeCurrency(metrics.totalExtendedValue)}
        </span>
      </div>

      {/* Additional Metrics */}
      <div style={styles.additionalMetrics}>
        <div style={styles.additionalMetricItem}>
          <span style={styles.additionalLabel}>Scenario Total Cases</span>
          <span style={styles.additionalValue}>{formatNumber(metrics.totalPlannedCases)}</span>
        </div>
        <div style={styles.additionalMetricItem}>
          <span style={styles.additionalLabel}>Scenario Utilization</span>
          <span style={styles.additionalValue}>{formatPercent(metrics.overallUtilization)}</span>
        </div>
        <div style={styles.additionalMetricItem}>
          <span style={styles.additionalLabel}>Open Capacity</span>
          <span style={styles.additionalValue}>{formatNumber(metrics.totalOpenCapacity)}</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  placeholder: {
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
  },
  placeholderText: {
    margin: '0 0 1rem 0',
    color: '#666',
    fontSize: '0.9rem',
  },
  baselineInfo: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  baselineItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  baselineLabel: {
    fontSize: '0.75rem',
    color: '#888',
  },
  baselineValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
  },
  comparisonSection: {
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
  },
  comparisonRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  metricBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  metricLabel: {
    fontSize: '0.7rem',
    color: '#666',
    textTransform: 'uppercase',
  },
  metricValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#333',
  },
  arrow: {
    fontSize: '1.25rem',
    color: '#999',
    padding: '0 0.5rem',
  },
  savingsBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    border: '1px solid',
  },
  savingsLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  savingsValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  extendedValueBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
  },
  extendedValueLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
  },
  extendedValueAmount: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  additionalMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #eee',
  },
  additionalMetricItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  additionalLabel: {
    fontSize: '0.65rem',
    color: '#888',
    textTransform: 'uppercase',
  },
  additionalValue: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#333',
  },
};

export default ScenarioMetricsDisplay;
